import React, { useCallback, useState, useEffect, useMemo } from 'react';
import ReactCalendar from 'react-calendar';
import Dropdown from 'react-dropdown';
import moment from 'moment';
import { CustomPriceModel } from '../../../ui-models/unit-type.ui-model';
import NumberFormat from 'react-number-format';

const today = new Date();
const tomorrow = new Date(today);
tomorrow.setDate(tomorrow.getDate() + 1);

/**
 * CalendarProps interface
 */
export interface CalendarProps {
  eventHandlers: {
    onChange: (date: Date | Date[]) => void;
  };
  data?: {
    calenderView?: boolean;
    disbaleDate?: Date[];
    addInitValue?: boolean;
    showDateInput?: boolean;
    basePriceTag?: number;
    customPriceTags?: CustomPriceModel[];
    selectedDate?: Date[] | Array<[Date, Date]>;
    multipleRanges?: boolean;
    dateRanges?: Array<[Date, Date]>;
  };
}

/**
 * Functional component Calendar
 * Displays calendar for date selection with price information
 */
export const Calendar: React.FC<CalendarProps> = ({ eventHandlers, data }) => {
  const monthOptions = [
    { value: '0', label: 'Jan' },
    { value: '1', label: 'Feb' },
    { value: '2', label: 'Mar' },
    { value: '3', label: 'Apr' },
    { value: '4', label: 'May' },
    { value: '5', label: 'June' },
    { value: '6', label: 'July' },
    { value: '7', label: 'Aug' },
    { value: '8', label: 'Sep' },
    { value: '9', label: 'Oct' },
    { value: '10', label: 'Nov' },
    { value: '11', label: 'Dec' }
  ];

  const yearOptions = (() => {
    const max = new Date().getFullYear() + 9;
    const min = max - 9;
    const years: string[] = [];
    for (let i = max; i >= min; i--) {
      years.push(i.toString());
    }
    return years.reverse();
  })();

  const [value, onChange] = useState(data?.addInitValue ? tomorrow : null);
  const [monthValue, setMonthValue] = useState(monthOptions[tomorrow.getMonth()]);
  const [yearValue, setYearValue] = useState(tomorrow.getFullYear().toFixed());
  const [activeStartDate, setActiveStartDate] = useState(tomorrow);
  const [isActive, setIsActive] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date[] | Array<[Date, Date]>>([]);
  const [dateRanges, setDateRanges] = useState<Array<[Date, Date]>>(data?.dateRanges || []);

  const isDateInRanges = useMemo(
    () => (date: Date) => {
      if (!dateRanges || !Array.isArray(dateRanges)) {
        return false;
      }
      return dateRanges.some((range) => {
        if (Array.isArray(range) && range.length === 2) {
          const [start, end] = range;
          return date >= start && date <= end;
        }
        return false;
      });
    },
    [dateRanges]
  );

  const handleDateClick = useCallback(
    (clickedDate: Date) => {
      if (data?.multipleRanges) {
        const rangeIndex = dateRanges.findIndex((range) => {
          if (Array.isArray(range) && range.length === 2) {
            const [start, end] = range;
            return (
              start instanceof Date &&
              end instanceof Date &&
              clickedDate >= start &&
              clickedDate <= end
            );
          }
          return false;
        });

        if (rangeIndex !== -1) {
          const newRanges = [...dateRanges];
          newRanges.splice(rangeIndex, 1);
          setDateRanges(newRanges);
          eventHandlers.onChange(newRanges.flat());
        }
      }
    },
    [dateRanges, data?.multipleRanges, eventHandlers]
  );

  function onYearOrMonthChange(month?: any, year?: any) {
    month
      ? setActiveStartDate(new Date(Number(year ? year : yearValue), Number(month.value), 1))
      : setActiveStartDate(new Date(Number(year), Number(monthValue.value), 1));
  }

  useEffect(() => {
    if (Array.isArray(data?.selectedDate) && data.selectedDate.length > 0) {
      setSelectedDate(data.selectedDate);
    } else if (!data?.multipleRanges) {
      setSelectedDate([]);
    }
  }, [data]);

  return (
    <div className="w-full">
      {data && data.calenderView ? (
        <div className="relative w-full">
          {/* Calendar Controls */}
          <div className="relative">
            {/* Month/Year Dropdown Controls */}
            <div className="flex gap-4 mb-6 items-center justify-center">
              <div className="w-32">
                <Dropdown
                  options={monthOptions}
                  value={monthValue}
                  onChange={(val: any) => {
                    setMonthValue(val);
                    onYearOrMonthChange(val);
                  }}
                  placeholder="Select month"
                  className="react-dropdown-custom"
                />
              </div>
              <div className="w-32">
                <Dropdown
                  options={yearOptions}
                  value={yearValue}
                  onChange={(val: any) => {
                    setYearValue(val.value);
                    onYearOrMonthChange(null, val.value);
                  }}
                  placeholder="Select year"
                  className="react-dropdown-custom"
                />
              </div>
            </div>

            {/* Calendar */}
            <ReactCalendar
              tileDisabled={(date: any) => {
                if (data && Array.isArray(data.disbaleDate)) {
                  return data.disbaleDate
                    .map((date) => {
                      return new Date(date.toDateString()).toString();
                    })
                    .includes(new Date(date.date.toDateString()).toString());
                }
                return false;
              }}
              {...(data?.multipleRanges && {
                value: value
              })}
              onClickDay={(date) => handleDateClick(date)}
              onChange={(val: any) => {
                if (data?.multipleRanges) {
                  if (Array.isArray(val) && val.length === 2) {
                    const hasOverlap = dateRanges.some((range) => {
                      if (Array.isArray(range) && range.length === 2) {
                        const existingStart = range[0];
                        const existingEnd = range[1];
                        return val[0] <= existingEnd && val[1] >= existingStart;
                      }
                      return false;
                    });

                    if (!hasOverlap) {
                      const newRanges: Array<[Date, Date]> = [...dateRanges, [val[0], val[1]]];
                      setDateRanges(newRanges);
                      eventHandlers.onChange(newRanges.flat());
                    }
                  }
                } else {
                  if (
                    Array.isArray(val) &&
                    val.length === 2 &&
                    val[0].toDateString().toString() === val[1].toDateString().toString()
                  ) {
                    onChange(null as any);
                    eventHandlers.onChange(null as any);
                  } else {
                    onChange(val);
                    eventHandlers.onChange(val);
                  }
                }
              }}
              selectRange={true}
              next2Label={null}
              prev2Label={null}
              navigationLabel={() => null}
              allowPartialRange={true}
              tileContent={({ activeStartDate, date, view }) => {
                if (
                  view === 'month' &&
                  data &&
                  data.customPriceTags &&
                  data.customPriceTags.length > 0
                ) {
                  const matchedCustomPrice = data.customPriceTags.find(
                    (customPrice) =>
                      date <= new Date(moment(customPrice.to).format('MM-DD-YYYY')) &&
                      date >= new Date(moment(customPrice.from).format('MM-DD-YYYY'))
                  );
                  if (matchedCustomPrice) {
                    return (
                      <p className="text-xs font-semibold text-blue-600 mt-1">
                        <NumberFormat
                          value={matchedCustomPrice.price}
                          thousandSeparator={true}
                          displayType={'text'}
                          prefix={'EGP '}
                        />
                      </p>
                    );
                  } else {
                    if (data.basePriceTag) {
                      return (
                        <p className="text-xs font-semibold text-gray-600 mt-1">
                          <NumberFormat
                            value={data.basePriceTag}
                            thousandSeparator={true}
                            displayType={'text'}
                            prefix={'EGP '}
                          />
                        </p>
                      );
                    }
                  }
                } else {
                  if (data.basePriceTag) {
                    return (
                      <p className="text-xs font-semibold text-gray-600 mt-1">
                        <NumberFormat
                          value={data.basePriceTag}
                          thousandSeparator={true}
                          displayType={'text'}
                          prefix={'EGP '}
                        />
                      </p>
                    );
                  }
                }
                if (data?.multipleRanges && isDateInRanges(date)) {
                  return <div className="absolute inset-0 bg-blue-100 opacity-50 rounded-sm" />;
                }
                return null;
              }}
              activeStartDate={activeStartDate}
              tileClassName={({ date }) => {
                const reservationDate =
                  Array.isArray(data.disbaleDate) &&
                  data.disbaleDate.find((disabledDate) => {
                    return (
                      date.toDateString().toString() === disabledDate.toDateString().toString()
                    );
                  });
                if (reservationDate) {
                  return '';
                }
                return '';
              }}
              minDate={
                ((Array.isArray(value) && value.length === 1) || value) && data?.disbaleDate?.length
                  ? (() => {
                      if (data && Array.isArray(data.disbaleDate)) {
                        const sortDisableDate = data.disbaleDate.sort(
                          (a, b) => b.getTime() - a.getTime()
                        );
                        const firstBiggerDate = sortDisableDate.find(
                          (sortedDate) =>
                            sortedDate.getTime() <
                            (Array.isArray(value) ? value[0]?.getTime() : value?.getTime())
                        );
                        return firstBiggerDate;
                      }
                    })()
                  : undefined
              }
              maxDate={
                ((Array.isArray(value) && value.length === 1) || value) && data?.disbaleDate?.length
                  ? (() => {
                      if (data && Array.isArray(data.disbaleDate)) {
                        const sortDisableDate = data.disbaleDate.sort(
                          (a, b) => a.getTime() - b.getTime()
                        );
                        const firstBiggerDate = sortDisableDate.find(
                          (sortedDate) =>
                            sortedDate.getTime() >
                            (Array.isArray(value) ? value[0]?.getTime() : value?.getTime())
                        );
                        return firstBiggerDate;
                      }
                    })()
                  : undefined
              }
              onActiveStartDateChange={({ activeStartDate }) => {
                setYearValue(String(activeStartDate.getFullYear()));
                const findMonth = monthOptions.find((opt) => {
                  return opt.value === String(activeStartDate.getMonth());
                });
                if (findMonth) {
                  setMonthValue(findMonth);
                  onYearOrMonthChange(findMonth, String(activeStartDate.getFullYear()));
                }
              }}
            />
          </div>

          {/* Date Ranges Display */}
          {dateRanges?.length > 0 && (
            <div className="mt-8">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                Selected Dates ({dateRanges.length})
              </h3>
              <div className="space-y-3">
                {dateRanges?.map((range, index) => {
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200 hover:border-blue-300 transition-colors group"
                    >
                      <div className="flex-1 flex items-center gap-3">
                        <div className="flex flex-col">
                          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                            From
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {range[0]?.toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-200">
                          <svg
                            className="w-4 h-4 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                            To
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {range[1]?.toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDateClick(range[0])}
                        className="flex-shrink-0 p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700 transition-colors opacity-0 group-hover:opacity-100"
                        title="Remove this date range"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="w-full">
          {/* Input View */}
          <div
            className="border border-gray-300 rounded-lg p-4 bg-white cursor-pointer hover:border-gray-400 transition-colors"
            onClick={() => setIsActive(!isActive)}
          >
            <div className="text-sm font-medium text-gray-700 mb-2">Select Date Range</div>
            <div className="text-gray-900 font-semibold">
              {selectedDate?.length > 0
                ? `${
                    Array.isArray(selectedDate[0])
                      ? selectedDate[0][0].toLocaleDateString()
                      : selectedDate[0].toLocaleDateString()
                  } - ${
                    Array.isArray(selectedDate[1])
                      ? selectedDate[1][1].toLocaleDateString()
                      : selectedDate[1].toLocaleDateString()
                  }`
                : 'Choose dates...'}
            </div>
          </div>

          {/* Calendar Dropdown */}
          {isActive ? (
            <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-6 z-50 w-full md:w-96">
              <div className="flex gap-4 mb-6">
                <div className="flex-1">
                  <Dropdown
                    options={monthOptions}
                    value={monthValue}
                    onChange={(val: any) => {
                      setMonthValue(val);
                      onYearOrMonthChange(val);
                    }}
                    placeholder="Select month"
                  />
                </div>
                <div className="flex-1">
                  <Dropdown
                    options={yearOptions}
                    value={yearValue}
                    onChange={(val: any) => {
                      setYearValue(val.value);
                      onYearOrMonthChange(null, val.value);
                    }}
                    placeholder="Select year"
                  />
                </div>
              </div>

              <ReactCalendar
                tileDisabled={(date: any) => {
                  if (data && data.disbaleDate) {
                    return data.disbaleDate.includes(date);
                  }
                  return false;
                }}
                onChange={(val: any) => {
                  onChange(val);
                  eventHandlers.onChange(val as Date[]);
                  setIsActive(!isActive);
                  setSelectedDate(val);
                }}
                value={value}
                selectRange={true}
                next2Label={null}
                prev2Label={null}
                navigationLabel={() => null}
                activeStartDate={activeStartDate}
                onActiveStartDateChange={({ activeStartDate }) => {
                  setYearValue(String(activeStartDate.getFullYear()));
                  const findMonth = monthOptions.find((opt) => {
                    return opt.value === String(activeStartDate.getMonth());
                  });
                  if (findMonth) {
                    setMonthValue(findMonth);
                    onYearOrMonthChange(findMonth, String(activeStartDate.getFullYear()));
                  }
                }}
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
